//
// Copyright 2026 DXOS.org
//

import * as Context from 'effect/Context';
import * as Effect from 'effect/Effect';
import * as Layer from 'effect/Layer';
import { type DependencyList, use, useCallback, useMemo } from 'react';

import * as Operation from '@dxos/compute/Operation';
import * as ServiceResolver from '@dxos/compute/ServiceResolver';
import { EffectEx } from '@dxos/effect';
import type { SpaceId } from '@dxos/keys';

import { Capabilities } from '../../common';
import { useCapability } from './useCapabilities';

/**
 * Resolve the shared {@link Capabilities.ProcessManagerRuntime} from the plugin context.
 */
export const useProcessManagerRuntime = (): Capabilities.ProcessManagerRuntime =>
  useCapability(Capabilities.ProcessManagerRuntime);

/**
 * Build a callback that runs an effect on the {@link Capabilities.ProcessManagerRuntime}
 * with space-scoped services resolved via {@link ServiceResolver.provide}.
 *
 * The `tags` tuple must list every service the effect requires (beyond the
 * fixed {@link Capabilities.ProcessManagerRuntimeServices}); these services are
 * resolved for the given `spaceId` through the runtime's service resolver.
 *
 * Nested `Operation.invoke` / `Operation.schedule` calls within the effect
 * inherit `spaceId` as a default {@link Operation.InvokeOptions} so that
 * spawned operation processes have a space context (and therefore can resolve
 * space-scoped services like `Database.Service`) without each call site having
 * to thread the id through manually.
 */
export const useSpaceCallback = <const Tags extends readonly Context.Key<any, any>[], T>(
  spaceId: SpaceId | undefined,
  tags: Tags,
  fn: () => Effect.Effect<
    T,
    any,
    Context.Service.Identifier<Tags[number]> | Capabilities.ProcessManagerRuntimeServices
  >,
  deps?: DependencyList,
): (() => Promise<T>) => {
  const runtime = useProcessManagerRuntime();
  return useCallback(() => {
    if (spaceId === undefined) {
      throw new TypeError('Space not provided to useSpaceCallback');
    }
    const layer = Layer.merge(
      ServiceResolver.provide({ space: spaceId }, ...tags),
      Operation.withInvocationOptions({ spaceId }),
    );
    return runtime.runPromise(fn().pipe(Effect.provide(layer)) as Effect.Effect<T, any, any>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtime, spaceId, ...(deps ?? [])]);
};

/**
 * Suspensefully resolve a single space-scoped service via the
 * {@link Capabilities.ProcessManagerRuntime}'s service resolver.
 */
export const useSpaceService = <T extends Context.Key<any, any>>(
  tag: T,
  spaceId: SpaceId | undefined,
): Context.Service.Shape<T> | undefined => {
  const runtime = useProcessManagerRuntime();
  const promise = useMemo(() => {
    if (spaceId === undefined) {
      return undefined;
    }
    const layer = ServiceResolver.provide({ space: spaceId }, tag);
    const effect = Effect.flatMap(tag, (service) => Effect.succeed(service)).pipe(Effect.provide(layer));
    return runtime.runPromiseExit(effect as Effect.Effect<Context.Service.Shape<T>, any, any>);
  }, [runtime, spaceId, tag]);
  if (!promise) {
    return undefined;
  }
  return EffectEx.unwrapExit(use(promise));
};
